/**
 * Local persistence for the gateway connection and its issued device token.
 *
 * After the first successful pair the Gateway hands back a device token in
 * `hello-ok.auth.deviceToken`. Persisting it is what makes "open the app and
 * you're already connected" work — the user never re-enters the setup token.
 *
 * Records are keyed by gateway origin so pointing the app at a second Gateway
 * does not clobber the credentials of the first.
 */
import { gatewayCredentialScope } from "@openclaw/gateway-client/browser";
import type { GatewayBrowserDeviceTokenStore } from "@openclaw/gateway-client/browser";

const CONNECTION_KEY = "openclaw-messenger:connection:v1";
const TOKEN_KEY = "openclaw-messenger:device-tokens:v1";

export type ConnectionSettings = {
  /** ws:// or wss:// URL of the Gateway. */
  url: string;
  /**
   * The shared/bootstrap token the user pasted during setup. Kept so a reconnect
   * still works before the Gateway has issued a device token, and so a Gateway in
   * shared-token mode (which never issues one) keeps connecting.
   */
  token?: string;
};

type TokenRecord = { token: string; scopes: string[] };
type TokenTable = Record<string, TokenRecord>;

function safeStorage(): Storage | null {
  try {
    const storage = globalThis.localStorage;
    if (!storage) {
      return null;
    }
    const probe = "__openclaw_probe__";
    storage.setItem(probe, "1");
    storage.removeItem(probe);
    return storage;
  } catch {
    return null;
  }
}

function readJson<T>(key: string): T | null {
  try {
    const raw = safeStorage()?.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    safeStorage()?.setItem(key, JSON.stringify(value));
  } catch {
    // Storage-blocked contexts simply do not persist; the session still works.
  }
}

export function loadConnectionSettings(): ConnectionSettings | null {
  const stored = readJson<ConnectionSettings>(CONNECTION_KEY);
  if (!stored || typeof stored.url !== "string" || !stored.url.trim()) {
    return null;
  }
  return {
    url: stored.url.trim(),
    token: typeof stored.token === "string" && stored.token ? stored.token : undefined,
  };
}

export function saveConnectionSettings(settings: ConnectionSettings): void {
  writeJson(CONNECTION_KEY, settings);
}

export function clearConnectionSettings(): void {
  try {
    safeStorage()?.removeItem(CONNECTION_KEY);
  } catch {
    // Nothing to clear.
  }
}

function tokenKey(params: { gatewayUrl: string; clientId: string; deviceId: string; role: string }) {
  return [
    gatewayCredentialScope(params.gatewayUrl),
    params.clientId,
    params.deviceId,
    params.role,
  ].join("|");
}

/**
 * Device-token store bound to one gateway URL, in the shape
 * `GatewayBrowserDeviceAuthLifecycle` expects.
 */
export function createDeviceTokenStore(gatewayUrl: string): GatewayBrowserDeviceTokenStore {
  return {
    load: ({ clientId, deviceId, role }) => {
      const table = readJson<TokenTable>(TOKEN_KEY) ?? {};
      return table[tokenKey({ gatewayUrl, clientId, deviceId, role })] ?? null;
    },
    store: ({ clientId, deviceId, role, token, scopes }) => {
      const table = readJson<TokenTable>(TOKEN_KEY) ?? {};
      table[tokenKey({ gatewayUrl, clientId, deviceId, role })] = { token, scopes };
      writeJson(TOKEN_KEY, table);
    },
    clear: ({ clientId, deviceId, role }) => {
      const table = readJson<TokenTable>(TOKEN_KEY) ?? {};
      delete table[tokenKey({ gatewayUrl, clientId, deviceId, role })];
      writeJson(TOKEN_KEY, table);
    },
  };
}

const CLIENT_FIELDS_KEY = "openclaw-messenger:unsupported-client-fields:v1";

/**
 * Optional `connect.params.client` fields a given Gateway rejects.
 *
 * Remembered per gateway origin so an older build costs one failed connect
 * overall rather than one on every app start.
 */
export function loadUnsupportedClientFields(gatewayUrl: string): string[] {
  const table = readJson<Record<string, string[]>>(CLIENT_FIELDS_KEY) ?? {};
  const stored = table[gatewayCredentialScope(gatewayUrl)];
  return Array.isArray(stored) ? stored.filter((item) => typeof item === "string") : [];
}

export function saveUnsupportedClientFields(gatewayUrl: string, fields: string[]): void {
  const table = readJson<Record<string, string[]>>(CLIENT_FIELDS_KEY) ?? {};
  table[gatewayCredentialScope(gatewayUrl)] = fields;
  writeJson(CLIENT_FIELDS_KEY, table);
}

export function clearAllDeviceTokens(): void {
  try {
    safeStorage()?.removeItem(TOKEN_KEY);
    safeStorage()?.removeItem(CLIENT_FIELDS_KEY);
  } catch {
    // Nothing to clear.
  }
}
